/**
 * @remarks RuntimeDocumentV1 Entity、数据绑定与 Schema 保真处理
 * @module @yimoka/designable-playground/runtime
 */

import {
  RUNTIME_DOCUMENT_SIDECAR_PROP,
  visitFormilySchemaNodes,
} from '../shared/schema';
import {
  assertFormilySchemaDocument,
  assertFormilySchemaNode,
} from '../transformers';
import type {
  IFormilySchemaDocument,
} from '../transformers';

import {
  validateRuntimeDataBindingNode,
  validateRuntimeI18nFieldMap,
} from './document-bindings';
import {
  RUNTIME_ENTITY_KEYS,
  RUNTIME_METADATA_KEYS,
  RUNTIME_TEMPLATE_REFERENCE_KEYS,
  isObjectRecord,
  pushRuntimeIssue,
  validateAllowedKeys,
  validateRuntimeRequestConfig,
  validateRuntimeDictConfig,
} from './document-core';

import type {
  IRuntimeSchemaFidelityV1,
  IRuntimeDocumentValidationIssue,
} from './document-core';
import {
  validateRuntimeStaticComponentNames,
  validateRuntimeDesignableIds,
} from './document-expressions';
import {
  findRuntimeReactionSchemaPatches,
  resolveRuntimeSchemaAuditName,
  mergeRuntimeSchemaPatch,
  resolveRuntimeSchemaAuditNode,
  validateRuntimeComponentSecurity,
} from './document-security';

/** 校验 Formily 根文档形状，Runtime 不接受嵌套 form/schema package。 */
export const validateRuntimeSchemaRoot = (
  value: unknown,
  issues: IRuntimeDocumentValidationIssue[],
  path: string,
  schemaRoot: unknown = value,
) => {
  if (!isObjectRecord(value) || value.type !== 'object') {
    pushRuntimeIssue(
      issues,
      'invalid-runtime-schema-root',
      path,
      'Runtime schema 根节点必须是 type=object 的 Formily 文档',
    );
    return;
  }
  if (value.properties !== undefined && !isObjectRecord(value.properties)) {
    pushRuntimeIssue(
      issues,
      'invalid-runtime-schema-root',
      `${path}.properties`,
      'Runtime schema.properties 必须是对象',
    );
    return;
  }
  if (value['x-component'] !== undefined && value['x-component'] !== 'Form') {
    // Formily Transformer 只为根 Form 建立显式设计节点；其他根组件会在双向转换时丢失。
    pushRuntimeIssue(
      issues,
      'invalid-runtime-schema-root',
      `${path}.x-component`,
      'Runtime schema 根组件只支持 Form，其他容器请放入 properties',
    );
  }
  if (value['x-component-props'] !== undefined && value['x-component'] !== 'Form') {
    pushRuntimeIssue(
      issues,
      'invalid-runtime-schema-root',
      `${path}.x-component-props`,
      'Runtime schema 根组件属性只允许配合根 Form 使用',
    );
  }
  if (value['x-decorator'] !== undefined) {
    pushRuntimeIssue(
      issues,
      'invalid-runtime-schema-root',
      `${path}.x-decorator`,
      'Runtime schema 根装饰器无法双向转换，请放入 properties',
    );
  }
  if (value['x-decorator-props'] !== undefined) {
    pushRuntimeIssue(
      issues,
      'invalid-runtime-schema-root',
      `${path}.x-decorator-props`,
      'Runtime schema 根装饰器属性无法双向转换，请放入 properties',
    );
  }
  visitFormilySchemaNodes(value, path, ({ node, path: nodePath }) => {
    if (!Object.prototype.hasOwnProperty.call(node, RUNTIME_DOCUMENT_SIDECAR_PROP)) return;
    pushRuntimeIssue(
      issues,
      'invalid-runtime-document',
      `${nodePath}.${RUNTIME_DOCUMENT_SIDECAR_PROP}`,
      `${RUNTIME_DOCUMENT_SIDECAR_PROP} 是设计器内部保留键，Runtime Schema 不允许声明`,
    );
  });
  try {
    assertFormilySchemaDocument(value);
  } catch (error) {
    pushRuntimeIssue(
      issues,
      'invalid-runtime-schema-root',
      path,
      error instanceof Error
        ? `Runtime schema 无效：${error.message}`
        : 'Runtime schema 必须是合法的 Formily 文档',
    );
  }
  validateRuntimeStaticComponentNames(value, issues, path);
  validateRuntimeDesignableIds(value, issues, path);
  validateRuntimeComponentSecurity(value, issues, path, { schemaRoot });
};

/** 校验 fieldsConfig 中会作为 definition 合入运行时的 Formily 节点。 */
export const validateRuntimeSchemaNode = (
  value: unknown,
  issues: IRuntimeDocumentValidationIssue[],
  path: string,
  schemaRoot: unknown = value,
) => {
  try {
    assertFormilySchemaNode(value, path);
  } catch (error) {
    pushRuntimeIssue(
      issues,
      'invalid-runtime-schema-root',
      path,
      error instanceof Error
        ? `Runtime 字段 schema 无效：${error.message}`
        : 'Runtime 字段 schema 必须是合法的 Formily 节点',
    );
  }
  validateRuntimeStaticComponentNames(value, issues, path);
  validateRuntimeComponentSecurity(value, issues, path, { schemaRoot });
};

/** 校验 Runtime v1 的 Entity allowlist、请求配置和多语言字段形状。 */
export const validateRuntimeEntityConfig = (
  value: unknown,
  issues: IRuntimeDocumentValidationIssue[],
  path: string,
) => {
  if (!isObjectRecord(value)) {
    pushRuntimeIssue(issues, 'invalid-runtime-document', path, 'entityConfig 必须是对象');
    return;
  }

  validateAllowedKeys(value, RUNTIME_ENTITY_KEYS, issues, path, 'invalid-runtime-document');
  [
    'basePath',
    'idKey',
    'idsKey',
    'labelKey',
    'name',
  ].forEach((key) => {
    if (value[key] !== undefined && typeof value[key] !== 'string') {
      pushRuntimeIssue(
        issues,
        'invalid-runtime-document',
        `${path}.${key}`,
        `entityConfig.${key} 必须是字符串`,
      );
    }
  });
  [
    'defaultDetailValues',
    'defaultFormValues',
    'defaultQueryValues',
    'extensions',
    'fieldsConfig',
    'map',
    'options',
    'path',
  ].forEach((key) => {
    if (value[key] !== undefined && !isObjectRecord(value[key])) {
      pushRuntimeIssue(
        issues,
        'invalid-runtime-document',
        `${path}.${key}`,
        `entityConfig.${key} 必须是对象`,
      );
    }
  });
  if (value.breadcrumb !== undefined) {
    if (
      !Array.isArray(value.breadcrumb)
      || value.breadcrumb.some(item => !isObjectRecord(item))
    ) {
      pushRuntimeIssue(
        issues,
        'invalid-runtime-document',
        `${path}.breadcrumb`,
        'entityConfig.breadcrumb 必须是对象数组',
      );
    }
  }
  if (value.dictConfig !== undefined) {
    if (!Array.isArray(value.dictConfig)) {
      pushRuntimeIssue(
        issues,
        'invalid-runtime-document',
        `${path}.dictConfig`,
        'entityConfig.dictConfig 必须是数组',
      );
    } else {
      value.dictConfig.forEach((item, index) => {
        validateRuntimeDictConfig(item, issues, `${path}.dictConfig[${index}]`);
      });
    }
  }
  if (value.api !== undefined) {
    if (!isObjectRecord(value.api)) {
      pushRuntimeIssue(
        issues,
        'runtime-unsafe-request-config',
        `${path}.api`,
        'entityConfig.api 必须是请求配置映射',
      );
    } else {
      Object.entries(value.api).forEach(([operation, request]) => {
        validateRuntimeRequestConfig(request, issues, `${path}.api.${operation}`);
      });
    }
  }
  if (value.i18nFieldMap !== undefined) {
    validateRuntimeI18nFieldMap(value.i18nFieldMap, issues, `${path}.i18nFieldMap`);
  }
  if (value.i18nInSchema !== undefined) {
    validateRuntimeSchemaRoot(value.i18nInSchema, issues, `${path}.i18nInSchema`);
  }
  if (value.i18nOutSchema !== undefined) {
    validateRuntimeSchemaRoot(value.i18nOutSchema, issues, `${path}.i18nOutSchema`);
  }
  if (value.i18nLangAPI !== undefined) {
    validateRuntimeRequestConfig(value.i18nLangAPI, issues, `${path}.i18nLangAPI`);
  }
};

/** 校验 Runtime metadata 只保存场景、模板引用和 JSON-safe 扩展。 */
export const validateRuntimeMetadata = (
  value: unknown,
  issues: IRuntimeDocumentValidationIssue[],
  path: string,
) => {
  if (!isObjectRecord(value)) {
    pushRuntimeIssue(issues, 'invalid-runtime-document', path, 'metadata 必须是对象');
    return;
  }

  validateAllowedKeys(value, RUNTIME_METADATA_KEYS, issues, path, 'invalid-runtime-document');
  if (
    value.scenarioId !== undefined
    && (
      typeof value.scenarioId !== 'string'
      || !value.scenarioId.trim()
      || value.scenarioId !== value.scenarioId.trim()
    )
  ) {
    pushRuntimeIssue(
      issues,
      'invalid-runtime-document',
      `${path}.scenarioId`,
      'metadata.scenarioId 必须是无首尾空白的非空字符串',
    );
  }
  if (value.extensions !== undefined && !isObjectRecord(value.extensions)) {
    pushRuntimeIssue(
      issues,
      'invalid-runtime-document',
      `${path}.extensions`,
      'metadata.extensions 必须是对象',
    );
  }
  if (value.template !== undefined) {
    if (!isObjectRecord(value.template)) {
      pushRuntimeIssue(
        issues,
        'invalid-runtime-document',
        `${path}.template`,
        'metadata.template 必须是对象',
      );
    } else {
      validateAllowedKeys(
        value.template,
        RUNTIME_TEMPLATE_REFERENCE_KEYS,
        issues,
        `${path}.template`,
        'invalid-runtime-document',
      );
      if (
        typeof value.template.id !== 'string'
        || !value.template.id.trim()
        || value.template.id !== value.template.id.trim()
      ) {
        pushRuntimeIssue(
          issues,
          'invalid-runtime-document',
          `${path}.template.id`,
          '模板 id 必须是无首尾空白的非空字符串',
        );
      }
      if (
        typeof value.template.version !== 'number'
        || !Number.isInteger(value.template.version)
        || value.template.version <= 0
      ) {
        pushRuntimeIssue(
          issues,
          'invalid-runtime-document',
          `${path}.template.version`,
          '模板 version 必须是正整数',
        );
      }
    }
  }
};

/** 递归扫描 Formily Schema 中的静态数据绑定冲突。 */
export const validateRuntimeDataBindings = (
  value: unknown,
  issues: IRuntimeDocumentValidationIssue[],
  path: string,
  schemaRoot: unknown = value,
) => {
  visitFormilySchemaNodes(value, path, ({ node, path: nodePath }) => {
    const auditNode = resolveRuntimeSchemaAuditNode(schemaRoot, node);
    const componentName = resolveRuntimeSchemaAuditName(
      schemaRoot,
      auditNode,
      'x-component',
      undefined,
      false,
    );
    validateRuntimeDataBindingNode(auditNode, issues, nodePath, componentName);
    const componentProps = auditNode['x-component-props'];
    if (componentName === 'I18nEditor' && isObjectRecord(componentProps)) {
      if (Object.prototype.hasOwnProperty.call(componentProps, 'fieldMap')) {
        // 组件显式映射优先于已校验的 Entity scope，必须复用同一安全且一对一的映射合同。
        validateRuntimeI18nFieldMap(
          componentProps.fieldMap,
          issues,
          `${nodePath}.x-component-props.fieldMap`,
        );
      }
      (['inSchema', 'outSchema'] as const).forEach((propertyName) => {
        if (!Object.prototype.hasOwnProperty.call(componentProps, propertyName)) return;
        // 多语言抽屉会把这两个属性作为独立 Formily 根渲染，数据绑定合同也必须独立闭合。
        validateRuntimeDataBindings(
          componentProps[propertyName],
          issues,
          `${nodePath}.x-component-props.${propertyName}`,
        );
      });
    }
    findRuntimeReactionSchemaPatches(
      auditNode['x-reactions'],
      `${nodePath}.x-reactions`,
    ).forEach((patch) => {
      const mergedNode = mergeRuntimeSchemaPatch(auditNode, patch.value);
      if (isObjectRecord(mergedNode)) {
        const mergedComponentName = resolveRuntimeSchemaAuditName(
          schemaRoot,
          mergedNode,
          'x-component',
          componentName,
          true,
        );
        validateRuntimeDataBindingNode(
          mergedNode,
          issues,
          patch.path,
          mergedComponentName,
        );
      }
      // reaction 补丁中的嵌套字段也会独立进入 RecursionField，必须继续扫描其数据绑定。
      validateRuntimeDataBindings(patch.value, issues, patch.path, schemaRoot);
    });
  });
};

/** 复现 DynamicView/EntitySchema 对 Entity、Store 字段及输出 Schema 的 definitions 合成顺序。 */
export const createRuntimeEntitySchemaRoot = (
  schema: unknown,
  entityConfig: unknown,
  storeConfig: unknown,
) => {
  if (!isObjectRecord(schema)) return schema;
  const generatedDefinitions: Record<string, unknown> = {};
  const isListStore = isObjectRecord(storeConfig) && storeConfig.type === 'list';

  /** 登记一层字段配置；后登记的 Store 字段按真实运行时优先级覆盖 Entity 同名字段。 */
  const registerFieldsConfig = (config: unknown) => {
    if (!isObjectRecord(config) || !isObjectRecord(config.fieldsConfig)) return;
    Object.entries(config.fieldsConfig).forEach(([field, fieldSchema]) => {
      if (!isObjectRecord(fieldSchema)) return;
      const {
        'x-output-schema': outputSchema,
        'x-query-schema': querySchema,
        ...baseSchema
      } = fieldSchema;
      const querySafeBase = { ...baseSchema };
      if (isListStore) {
        delete querySafeBase.required;
        delete querySafeBase['x-validator'];
      }
      const effectiveFieldSchema = isListStore && isObjectRecord(querySchema)
        ? mergeRuntimeSchemaPatch(querySafeBase, querySchema)
        : querySafeBase;
      if (isObjectRecord(effectiveFieldSchema) && isListStore) {
        delete effectiveFieldSchema.required;
        delete effectiveFieldSchema['x-validator'];
      }
      generatedDefinitions[field] = effectiveFieldSchema;
      delete generatedDefinitions[`__output__${field}`];
      if (!isObjectRecord(outputSchema) || Object.keys(outputSchema).length === 0) return;
      generatedDefinitions[`__output__${field}`] = outputSchema.title === undefined
        ? { ...outputSchema, title: baseSchema.title }
        : outputSchema;
    });
  };

  registerFieldsConfig(entityConfig);
  registerFieldsConfig(storeConfig);
  const documentDefinitions = isObjectRecord(schema.definitions)
    ? schema.definitions
    : {};
  return {
    ...schema,
    // EntitySchema 允许文档自身 definitions 最后覆盖自动生成定义，审计必须遵守同一闭包与优先级。
    definitions: {
      ...generatedDefinitions,
      ...documentDefinitions,
    },
  };
};

/** 扫描 Store/Entity fieldsConfig 中最终会合入 Formily definitions 的字段 Schema。 */
export const validateRuntimeFieldsConfigDataBindings = (
  value: unknown,
  issues: IRuntimeDocumentValidationIssue[],
  path: string,
  schemaRoot?: unknown,
) => {
  if (!isObjectRecord(value) || !isObjectRecord(value.fieldsConfig)) return;
  // EntitySchema 会把全部 fieldsConfig 作为同一 definitions 闭包消费；校验必须复现这一引用根。
  const effectiveSchemaRoot = schemaRoot ?? {
    definitions: value.fieldsConfig,
    type: 'object',
  };
  Object.entries(value.fieldsConfig).forEach(([field, schema]) => {
    const schemaPath = `${path}.fieldsConfig.${field}`;
    validateRuntimeSchemaNode(schema, issues, schemaPath, effectiveSchemaRoot);
    validateRuntimeDataBindings(schema, issues, schemaPath, effectiveSchemaRoot);
    if (!isObjectRecord(schema)) return;
    (['x-query-schema', 'x-output-schema'] as const).forEach((propertyName) => {
      const derivedSchema = schema[propertyName];
      if (derivedSchema === undefined) return;
      const derivedSchemaPath = `${schemaPath}.${propertyName}`;
      if (value.type === 'list' && propertyName === 'x-query-schema') {
        const queryBaseSchema = { ...schema };
        delete queryBaseSchema.required;
        delete queryBaseSchema['x-validator'];
        delete queryBaseSchema['x-query-schema'];
        delete queryBaseSchema['x-output-schema'];
        // ListStore 会把基础字段与查询补丁递归合并；安全审计必须复现最终节点，避免组件身份变化后继承危险属性。
        const effectiveQuerySchema = mergeRuntimeSchemaPatch(queryBaseSchema, derivedSchema);
        validateRuntimeSchemaNode(effectiveQuerySchema, issues, derivedSchemaPath, effectiveSchemaRoot);
        validateRuntimeDataBindings(effectiveQuerySchema, issues, derivedSchemaPath, effectiveSchemaRoot);
        return;
      }
      validateRuntimeSchemaNode(derivedSchema, issues, derivedSchemaPath, effectiveSchemaRoot);
      validateRuntimeDataBindings(derivedSchema, issues, derivedSchemaPath, effectiveSchemaRoot);
    });
  });
};

/** 扫描 Entity 多语言输入输出 Schema 的静态数据绑定合同。 */
export const validateRuntimeI18nDataBindings = (
  value: unknown,
  issues: IRuntimeDocumentValidationIssue[],
  path: string,
) => {
  if (!isObjectRecord(value)) return;
  if (value.i18nInSchema !== undefined) {
    validateRuntimeDataBindings(
      value.i18nInSchema,
      issues,
      `${path}.i18nInSchema`,
    );
  }
  if (value.i18nOutSchema !== undefined) {
    validateRuntimeDataBindings(
      value.i18nOutSchema,
      issues,
      `${path}.i18nOutSchema`,
    );
  }
};

/**
 * 记录基础 Transformer 为原始字段补出的设计 ID 和顺序元数据。
 *
 * 这些 ID 仍留在设计树中支撑编辑和历史，但导出时会按记录移除，
 * 从而让未经编辑的 Runtime golden 保持真正的原子 round-trip。
 */
export const createRuntimeSchemaFidelity = (
  original: unknown,
  normalized: unknown,
): IRuntimeSchemaFidelityV1 => {
  const omitDesignableIdFor = new Set<string>();
  const omitIndexFor = new Set<string>();

  /** 只沿 Formily properties/items 递归，避免把组件普通数据误判成 Schema 节点。 */
  const visit = (source: unknown, target: unknown) => {
    if (!isObjectRecord(source) || !isObjectRecord(target)) return;

    const designableId = target['x-designable-id'];
    if (typeof designableId === 'string' && designableId) {
      if (!Object.prototype.hasOwnProperty.call(source, 'x-designable-id')) {
        omitDesignableIdFor.add(designableId);
      }
      if (!Object.prototype.hasOwnProperty.call(source, 'x-index')) {
        omitIndexFor.add(designableId);
      }
    }

    const sourceProperties = source.properties;
    const targetProperties = target.properties;
    if (isObjectRecord(sourceProperties) && isObjectRecord(targetProperties)) {
      Object.entries(sourceProperties).forEach(([key, child]) => {
        visit(child, targetProperties[key]);
      });
    }
    const sourceItems = source.items;
    const targetItems = target.items;
    if (Array.isArray(sourceItems) && Array.isArray(targetItems)) {
      sourceItems.forEach((item, index) => {
        visit(item, targetItems[index]);
      });
    } else if (sourceItems !== undefined && targetItems !== undefined) {
      visit(sourceItems, targetItems);
    }
  };

  visit(original, normalized);
  return {
    omitDesignableIdFor: Array.from(omitDesignableIdFor),
    omitIndexFor: Array.from(omitIndexFor),
  };
};

/** 按导入时的元数据存在性清理基础 Transformer 自动补出的导出字段。 */
export const restoreRuntimeSchemaFidelity = (
  schema: IFormilySchemaDocument,
  fidelity: unknown,
): IFormilySchemaDocument => {
  if (!isObjectRecord(fidelity)) return schema;
  const restoredSchema = JSON.parse(JSON.stringify(schema)) as IFormilySchemaDocument;

  const omitDesignableIdFor = new Set(
    Array.isArray(fidelity.omitDesignableIdFor)
      ? fidelity.omitDesignableIdFor.filter((item): item is string => typeof item === 'string')
      : [],
  );
  const omitIndexFor = new Set(
    Array.isArray(fidelity.omitIndexFor)
      ? fidelity.omitIndexFor.filter((item): item is string => typeof item === 'string')
      : [],
  );

  /** 递归清理当前仍存在且 ID 匹配的字段，新拖入节点不受影响。 */
  const visit = (value: unknown) => {
    if (!isObjectRecord(value)) return;
    const schemaNode = value;
    const designableId = schemaNode['x-designable-id'];
    if (typeof designableId === 'string') {
      if (omitIndexFor.has(designableId)) {
        delete schemaNode['x-index'];
      }
      if (omitDesignableIdFor.has(designableId)) {
        delete schemaNode['x-designable-id'];
      }
    }

    if (isObjectRecord(schemaNode.properties)) {
      Object.values(schemaNode.properties).forEach(visit);
    }
    if (Array.isArray(schemaNode.items)) {
      schemaNode.items.forEach(visit);
    } else if (schemaNode.items !== undefined) {
      visit(schemaNode.items);
    }
  };

  visit(restoredSchema);
  return restoredSchema;
};
